import React, { useEffect, useState, useRef } from "react";
import { ReactComponent as MoreCircle } from "@icons/more-circle.svg";

export type ContextMenuListItemProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    text: string;
  };

export const ContextMenuListItem = ({ text, className = "", ...rest }: ContextMenuListItemProps) => {
  return (
    <li role="none" className="list-none border-b last:border-none border-brand-purple-lighter">
      <button
        role="menuitem"
        tabIndex={0}
        {...rest}
        className={`
          w-full text-left cursor-pointer
          bg-brand-purple-lightest hover:bg-brand-purple-lighter focus:bg-brand-purple-lighter
          px-4 py-3 font-normal outline-none text-base
          ${className}
        `}
      >
        {text}
      </button>
    </li>
  );
};


export type ContextMenuProps = React.HTMLAttributes<HTMLDivElement> & {
  menuPosition?: "left" | "center";
  children: React.ReactNode;
  onOpen?: () => void;
  text?: string;
  ariaLabel?: string;
};

export const ContextMenu = ({
  menuPosition = "left",
  ariaLabel = "Open actions menu",
  children,
  onOpen,
  text,
  ...rest
}: ContextMenuProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);

  const toggle = () => {
    setOpen(prev => {
      if (!prev && onOpen) onOpen();
      return !prev;
    });
  };

  const close = () => setOpen(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        optionsRef.current &&
        !optionsRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  let positionClass;
  switch (menuPosition) {
    case "left":
      positionClass = "right-0";
      break;
    case "center":
      positionClass = "left-1/2 -translate-x-1/2";
      break;
  }

  return (
    <div className="flex justify-center" {...rest}>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="true"
          aria-controls="context-menu"
          className="flex items-center px-2 py-1 rounded"
        >
          {text && <span className="mr-2 text-sm">{text}</span>}
          <MoreCircle className="h-6 w-6 text-brand-purple" />
        </button>

        {open && (
          <ul
            id="context-menu"
            role="menu"
            ref={optionsRef}
            className={`
              absolute top-full mt-2 mx-2 z-50 
              min-w-[200px] rounded-lg shadow-md overflow-hidden 
              bg-white pl-0 
              ${positionClass}
            `}
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === "Enter") toggle();
            }}
          >
            {children}
          </ul>
        )}
      </div>
    </div>
  );
};
